'use client';

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import { AppDispatch } from '@/app/store';
import {
  initializeConsent,
  openPreferences,
  selectHasResponded,
  selectIsConsentInitialized,
} from '@/app/store/slices/cookieConsentSlice';
import { CookieBanner } from './CookieBanner';
import { CookiePreferencesModal } from './CookiePreferencesModal';
import { Button } from '@/components/ui/button';
import { Cookie } from 'lucide-react';

// ─── CookieSettingsButton ─────────────────────────────────────────────────────

/** Small floating button in the bottom-left corner shown after consent is given. */
const CookieSettingsButton = React.memo(() => {
  const t = useTranslations('cookieConsent');
  const dispatch = useDispatch<AppDispatch>();
  const hasResponded = useSelector(selectHasResponded);

  if (!hasResponded) return null;

  return (
    <div className="fixed left-4 z-40 bottom-[calc(60px+env(safe-area-inset-bottom)+1rem)] md:bottom-4">
      <Button
        variant="outline"
        size="sm"
        className="h-8 px-2.5 text-xs gap-1.5 shadow-sm bg-background/95 backdrop-blur-sm"
        onClick={() => dispatch(openPreferences())}
        aria-label={t('settingsButton')}
        title={t('settingsButton')}
      >
        <Cookie className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t('settingsButton')}</span>
      </Button>
    </div>
  );
});
CookieSettingsButton.displayName = 'CookieSettingsButton';

// ─── CookieConsentProvider ────────────────────────────────────────────────────

/**
 * Initializes cookie consent from localStorage on mount,
 * then renders the banner (if needed) and the preferences modal.
 * Place this inside the Redux Provider in layout.tsx.
 */
export const CookieConsentProvider = React.memo(({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isInitialized = useSelector(selectIsConsentInitialized);

  useEffect(() => {
    dispatch(initializeConsent());
  }, [dispatch]);

  return (
    <>
      {children}
      {isInitialized && (
        <>
          <CookieBanner />
          <CookiePreferencesModal />
          <CookieSettingsButton />
        </>
      )}
    </>
  );
});
CookieConsentProvider.displayName = 'CookieConsentProvider';
